import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

/**
 * The `<meta name="btsis-environment">` each SPA's `index.html` carries. Caddy
 * fills its `content` from the stack's `BTSIS_ENVIRONMENT` when it serves the
 * page (`templates` in infra/docker/Caddyfile), and deploy.yml sets that from
 * the target it deploys — so the same image says `dev` on one stack and
 * `prod` on the other. Under Vite nothing fills it, and the literal template
 * text is no environment.
 */
export const ENVIRONMENT_META_NAME = 'btsis-environment';

/** Only the dev stack is a test site; any other value, or none, is not. */
export function isTestEnvironment(served: string | null | undefined): boolean {
  return served?.trim() === 'dev';
}

function servedEnvironment(): string | null {
  if (typeof document === 'undefined') return null;
  return (
    document.querySelector(`meta[name="${ENVIRONMENT_META_NAME}"]`)?.getAttribute('content') ?? null
  );
}

export type EnvironmentBannerProps = {
  /** On a page with no header to sit in — a sign-in page — it floats over the top edge. */
  floating?: boolean;
  /** For a test: the value the page was served with, instead of reading the document. */
  served?: string | null;
  sx?: SxProps<Theme>;
};

/** A strip saying this is the test site, on the dev stack only. */
export function EnvironmentBanner({ floating = false, served, sx }: EnvironmentBannerProps) {
  const { t } = useTranslation();
  if (!isTestEnvironment(served === undefined ? servedEnvironment() : served)) return null;

  return (
    <Box
      role="status"
      sx={[
        {
          px: 2,
          py: 0.5,
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          typography: 'caption',
          fontWeight: 700,
          textAlign: 'center',
        },
        floating && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar + 1,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {t('common.environment.test')}
    </Box>
  );
}
